import Axios from 'axios';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import debounce from 'lodash/debounce';
import type { UseTranslationOptions } from 'react-i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

import { appEnv, authApiHost } from './app-config';
import { DEFAULT_LOCALE, isValidLocale } from './utils/locale';
import { logError, logErrorWithContext } from './utils/log';

const appName = 'pureprofile-app' as const;
export type Namespace = typeof appName | 'survey-tool' | 'languages';

interface AuthApiLanguage {
  code: string;
  fallback_code?: string;
  name: string;
}

const EnglishLanguage: AuthApiLanguage = { code: 'en', name: 'English' };

const i18nextReloadResources = debounce(() => i18n.reloadResources(), 5000);

/**
 * True once `i18n.init()` has been *called* — deliberately not `i18n.isInitialized`.
 *
 * `init()` wires up `i18n.services` synchronously but defers the rest behind a `setTimeout`, so
 * `isInitialized` stays false for a further macrotask. During that window `changeLanguage` is
 * already safe, and gating on `isInitialized` would make the drain below re-queue itself with
 * nothing left to drain it.
 */
let i18nInitCalled = false;

/** A language requested before init; applied by the drain once i18next can accept it. */
let pendingLanguage: string | null = null;

function initI18N(languages: AuthApiLanguage[]) {
  i18n
    // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
    // learn more: https://github.com/i18next/i18next-http-backend
    // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
    .use(Backend)
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    .use(LanguageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
      ns: appName,
      lowerCaseLng: true, // we store them like this in the db
      supportedLngs: languages.map((l) => l.code),
      fallbackLng: (code: string | undefined) => {
        const language = languages.find((l) => l.code === code);
        if (!language) {
          // default to the first language returned by auth-api
          return languages[0].fallback_code ?? languages[0].code;
        }
        return language.fallback_code ?? [];
      },
      detection: {
        // Runs over every detector's raw output (querystring, cookie, localStorage,
        // sessionStorage, navigator, htmlTag) before i18next picks the best supported
        // match. A previously-poisoned `i18nextLng` of "null" (PUR-4480) would otherwise
        // reach language resolution unfiltered on every future load — which is why the
        // breakage persisted across sessions. i18next re-caches whatever it resolves, so
        // the bad localStorage value is overwritten on the affected user's next load.
        convertDetectedLanguage: (lng: string) => (isValidLocale(lng) ? lng : DEFAULT_LOCALE),
      },
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
      debug: appEnv === 'development',
      saveMissing: true,
      saveMissingTo: 'fallback',
      backend: {
        loadPath: `${authApiHost}/api/v1/translations/load/{{ns}}/{{lng}}`,
        addPath: `${authApiHost}/api/v1/translations/add/{{ns}}/{{lng}}`,
        crossDomain: true,
        withCredentials: true,
        customHeaders: {
          'X-App-Name': appName,
        },
        parsePayload: (namespace: any, key: any, fallbackValue: any) => {
          i18nextReloadResources();
          // log.info(`missing namespace=${namespace} key=${key} fallbackValue=${fallbackValue}`);
          return { [key]: fallbackValue || '' };
        },
      },
    });
  i18nInitCalled = true;
}

const domain = window.location.hostname;
Axios.get<AuthApiLanguage[]>(
  `${authApiHost}/api/v1/translations/languages/supported?domain=${encodeURIComponent(domain)}`
)
  .then((response) => response.data)
  .catch((err) => {
    logError(err);
    return [] as AuthApiLanguage[];
  })
  .then((supportedLngs) => {
    if (supportedLngs.length === 0) {
      supportedLngs.push(EnglishLanguage);
    }
    initI18N(supportedLngs);

    // Apply whatever the app asked for while we were still fetching the language list. Safe here
    // even though `isInitialized` is still false: `init()` has wired `i18n.services`, and because
    // this sets `i18n.languages` synchronously, i18next's own deferred first-load short-circuits
    // instead of re-resolving the language from the detector and overwriting this.
    if (pendingLanguage !== null) {
      const lng = pendingLanguage;
      pendingLanguage = null;
      safeChangeLanguage(lng);
    }
  })
  // Nothing followed the chain before, so a throw out of `initI18N` became an unhandled rejection
  // while the app sat on its Suspense fallback forever. If init does throw, `i18nInitCalled` stays
  // false and any queued language stays queued — deliberately: the singleton is unusable either
  // way, and handing it a language would throw again and bury this error under a second one.
  .catch((err) => logError(err));

export default i18n;

/**
 * The only sanctioned way to change language — never call `i18n.changeLanguage` directly.
 *
 * i18next applies a truthy `lng` verbatim: it only consults the detector (and therefore
 * `convertDetectedLanguage` above) when `lng` is falsy. It then unconditionally hands the
 * value to `cacheUserLanguage`, which writes it straight to `localStorage.i18nextLng`. So a
 * single unvalidated call both poisons `i18n.language` for the session and persists the bad
 * value across future ones — that is exactly how PUR-4480's `"null"` locale took hold and
 * survived reloads.
 *
 * An invalid tag is ignored rather than replaced, leaving the current valid language in place.
 *
 * A language requested before init is queued rather than applied, because `init()` is deferred
 * behind the auth-api call above: until it runs, `i18n.services` is `{}` and `changeLanguage`
 * throws `Cannot read properties of undefined (reading 'toResolveHierarchy')`. That is PUR-4781 —
 * a `?lang=` reset-password link crashed the render whenever it beat the language list back.
 * Queuing makes this function safe from any call site at any time; last request wins, which is
 * what back-to-back unqueued calls would have done.
 *
 * Two i18next behaviours that are easy to misread as bugs here, neither introduced by the queue:
 * `isInitialized` can flip before the namespace fetch finishes, but react-i18next's `ready` also
 * requires the namespace, so Suspense still holds; and a language outside `supportedLngs` is still
 * assigned to `i18n.language` verbatim, while `i18n.languages` — what actually gets fetched and
 * rendered — resolves through `fallbackLng`.
 */
export function safeChangeLanguage(lng: string | null | undefined) {
  if (!isValidLocale(lng)) {
    logErrorWithContext('ignored invalid language change', { lng: String(lng) });
    return;
  }
  if (!i18nInitCalled) {
    pendingLanguage = lng;
    return;
  }
  if (i18n.language !== lng) {
    i18n.changeLanguage(lng);
  }
}

export function useAppTranslation(ns?: Namespace, options?: UseTranslationOptions<any>) {
  return useTranslation(ns, options);
}
